// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import loadDashboardPanels from "@guestsupply/App/Handlers/DashboardPanels/LoadDashboardPanels";
import OrderApproval from "@guestsupply/XDL/Icons/OrderApproval";
import Quotes from "@guestsupply/XDL/Icons/Quotes";
import Requisitions from "@guestsupply/XDL/Icons/Requisitions";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { DashboardPanelCollectionModel, DashboardPanelModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Clickable, { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapDispatchToProps = {
    loadDashboardPanels,
};

type Props = ResolveThunks<typeof mapDispatchToProps>;

export interface DashboardPanelsStyles {
    panlesWrapper?: GridContainerProps;
    itemWrapper?: GridItemProps;
    clickablePanel?: ClickablePresentationProps;
    blockContent?: InjectableCss;
    iconContainer?: InjectableCss;
    textWrapper?: InjectableCss;
    panelText?: TypographyProps;
    panelCount?: TypographyProps;
}

export const dashboardPanelsStyles: DashboardPanelsStyles = {};

const styles = dashboardPanelsStyles;

const DashboardPanels = ({ loadDashboardPanels }: Props) => {
    const [panels, setPanels] = useState<DashboardPanelCollectionModel | undefined>(undefined);
    const orderApprovalsUrl = useAppSelector(state => getPageLinkByPageType(state, "OrderApprovalListPage"));

    useEffect(() => {
        loadDashboardPanels({
            onSuccess: result => {
                setPanels(result);
            },
        });
    }, []);

    const filteredPanels = panels?.dashboardPanels?.filter(x => x.isPanel);

    if (!filteredPanels) {
        return null;
    }

    // fix OrderApprovals panel url
    const updatedDashboardPanels = filteredPanels.map(panel =>
        panel.panelType === "OrderApprovals" ? { ...panel, url: orderApprovalsUrl?.url } : panel,
    );

    // panel-icon map
    const panelTypeIconMap: { [key: string]: React.ComponentType } = {
        RequestForQuote: Quotes,
        OrderApprovals: OrderApproval,
        Requisitions,
    };
    const getIconSrc = (panelType: string) => panelTypeIconMap[panelType] || "";

    return (
        <GridContainer {...styles.panlesWrapper}>
            {updatedDashboardPanels.map(panel => (
                <GridItem {...styles.itemWrapper} key={panel.panelType}>
                    <Clickable {...styles.clickablePanel} href={panel.url} aria-label={panel.text}>
                        <StyledWrapper {...styles.blockContent}>
                            <StyledWrapper {...styles.iconContainer}>
                                <Icon src={getIconSrc(panel.panelType)} size={35} />
                            </StyledWrapper>
                            <StyledWrapper {...styles.textWrapper}>
                                <Typography {...styles.panelCount}>{panel.count}</Typography>
                                <Typography {...styles.panelText}>{panel.text}</Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                    </Clickable>
                </GridItem>
            ))}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(DashboardPanels),
    definition: {
        group: "XNGAGE" as any,
        displayName: "Dashboard Panels",
    },
};

export default widgetModule;
